import {BaseNotification} from './base-notification';
import * as moment from 'moment';
import {DateUtils} from '../../../date-utils';

/**
 * Notification card for an on-demand RARS publish (RLP-4140).
 *
 * Rides the same /reopt/notifications feed the bell already polls, so publish
 * progress needs no extra request.
 */
export class RarsPublishNotification extends BaseNotification {
    public header = 'Send to RARS';
    public iconClass = 'fa-paper-plane';

    constructor(private notificationData: any) {
        super(notificationData);
        this.title = 'RARS';
        this.updateHeader();
        this.buildDescription();
    }

    public getRouteInfo(): any {
        const filters = {
            rsid: '',
            origin: '',
            destination: '',
            startDate: moment(this.notificationData.startDate, 'YYYY-MM-DD').toDate(),
            endDate: moment(this.notificationData.endDate, 'YYYY-MM-DD').toDate(),
            portfolio: [],
            statusType: 'Re-Optimized'
        };
        return {page: 'inventory', filters};
    }

    public routeDisabled(): boolean {
        // only worth navigating once something actually reached RARS, or partly did
        return ['Sent', 'Partial', 'Failed'].indexOf(this.notificationData.status) < 0;
    }

    private buildDescription(): void {
        const range = this.notificationData.startDate
            ? ` for ${DateUtils.convertToLocaleDateString(this.notificationData.startDate)} to `
              + `${DateUtils.convertToLocaleDateString(this.notificationData.endDate)}`
            : '';
        this.description = `Your send to RARS${range} ${this.getStatusText()}`;
    }

    private updateHeader(): void {
        if (this.notificationData.status === 'Pending Review') {
            this.header += ' – Awaiting Review';
        } else if (this.notificationData.status === 'Sending') {
            this.header += ' in Progress';
        }
    }

    private getStatusText(): string {
        const data = this.notificationData;
        switch (data.status) {
            case 'Requested':
            case 'Staging':
                return 'is being prepared.';
            case 'Pending Review':
                return `is ready to review (${data.journeyCount || 0} journeys).`;
            case 'Sending':
                return 'is being sent.';
            case 'Sent':
                return 'completed successfully.';
            case 'Partial':
                return `partly failed: ${data.successCount || 0} sent, ${data.failureCount || 0} failed.`;
            case 'Failed':
                return `failed. ${data.note || ''}`.trim();
            case 'Blocked':
                // SKIP_EXPORT is set: say so plainly rather than implying success
                return `was blocked: ${data.note || 'sending to RARS is currently disabled'}.`;
            case 'Cancelled':
                return 'was cancelled.';
            case 'Expired':
                return 'expired before it was reviewed.';
            default:
                return 'has unknown status';
        }
    }
}
